import { useRef, useState, DragEvent } from 'react';
import { FileText, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function LocalDropZone({
  files,
  onChange,
}: {
  files: File[];
  onChange: (files: File[]) => void;
}) {
  const [hover, setHover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const merge = (incoming: FileList | File[]) => {
    const arr = Array.from(incoming);
    const seen = new Set(files.map(f => f.name + f.size));
    const merged = [...files];
    for (const f of arr) {
      const key = f.name + f.size;
      if (!seen.has(key)) { merged.push(f); seen.add(key); }
    }
    onChange(merged);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setHover(false);
    if (e.dataTransfer.files) merge(e.dataTransfer.files);
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'cursor-pointer rounded-2xl border-2 border-dashed py-12 text-center transition-colors',
          hover ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/60'
        )}
      >
        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
        <p className="text-sm font-medium">Drag files here or click to browse</p>
        <p className="text-[11px] text-muted-foreground mt-1">Up to a few hundred files at a time</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => e.target.files && merge(e.target.files)}
        />
      </div>

      {files.length > 0 && (
        <div className="rounded-xl border max-h-[320px] overflow-auto divide-y">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="flex-1 truncate" title={f.name}>{f.name}</span>
              <span className="text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => { e.stopPropagation(); onChange(files.filter((_, j) => j !== i)); }}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
